import React, { useState, useEffect } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  CreditCard,
  RefreshCw,
  Download,
  Eye,
  Filter,
  Search
} from 'lucide-react';
import api from '../services/api';
import LoadingScreen from './LoadingScreen';

interface WalletBalance {
  id: number;
  merchant_code: string;
  virtual_card_number: string;
  currency: string;
  balance: number;
  created_at: string;
  updated_at: string;
}

interface WalletTransaction {
  id: number;
  transfer_type: 'DEPOSIT' | 'WITHDRAWAL' | 'TRANSFER';
  amount: number;
  currency: string;
  reference: string;
  description: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED';
  previous_balance: number;
  new_balance: number;
  created_at: string;
}

const WalletHistory: React.FC = () => {
  const { t, formatCurrency, formatDate } = useLocalization();
  const { user } = useAuth();
  const [wallets, setWallets] = useState<WalletBalance[]>([]);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWallet, setSelectedWallet] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [walletsRes, transactionsRes] = await Promise.all([
        api.get('/merchant/wallets'),
        api.get('/merchant/wallet-transactions', {
          params: {
            wallet_id: selectedWallet !== 'all' ? selectedWallet : undefined,
            search: searchTerm,
            start_date: dateRange.start || undefined,
            end_date: dateRange.end || undefined
          }
        })
      ]);

      setWallets(walletsRes.data);
      setTransactions(transactionsRes.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching wallet data:', err);
      setError(err.response?.data?.detail || 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
  }, [selectedWallet, searchTerm, dateRange]);

  const getTotalBalance = () => {
    return wallets.reduce((total, wallet) => total + wallet.balance, 0);
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'DEPOSIT': return 'text-green-600 bg-green-50 border-green-200';
      case 'WITHDRAWAL': return 'text-red-600 bg-red-50 border-red-200';
      case 'TRANSFER': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'DEPOSIT': return <TrendingUp className="w-4 h-4" />;
      case 'WITHDRAWAL': return <TrendingDown className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Banner */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 sm:p-6 text-white">
          <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)", backgroundSize: "28px 28px" }} />
          <div className="absolute -top-32 -right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-[100px]" />
          <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-teal-500/15 rounded-full blur-[100px]" />

          <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <Wallet className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">Wallet Balances & History</h1>
                <p className="text-slate-400 mt-1 text-sm">
                  Manage your virtual card wallets and track transaction history
                </p>
              </div>
            </div>

            <button
              onClick={fetchWalletData}
              disabled={loading}
              className="px-4 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/30 disabled:opacity-50 text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </button>
          </div>
        </div>

          {/* Wallet Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white shadow-lg shadow-emerald-500/20 hover:shadow-xl transition-all hover:scale-[1.02]"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold">{wallet.currency} Wallet</h3>
                      <p className="text-emerald-100 text-xs">Virtual Card</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedWallet(wallet.id.toString())}
                    className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>

                <div className="mb-4">
                  <div className="text-2xl font-black mb-1">
                    {formatCurrency(wallet.balance, wallet.currency)}
                  </div>
                  <div className="text-emerald-100 text-xs">Current Balance</div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <div>
                    <div className="text-emerald-100">Card Number</div>
                    <div className="font-mono text-white font-semibold">
                      •••• •••• •••• {wallet.virtual_card_number.slice(-4)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-100">Last Updated</div>
                    <div>{new Date(wallet.updated_at).toLocaleDateString()}</div>
                  </div>
                </div>
              </div>
            ))}

            {/* Total Balance Card */}
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold">Total Balance</h3>
                  <p className="text-purple-100 text-xs">All Wallets</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="text-2xl font-black mb-1">
                  {formatCurrency(getTotalBalance(), wallets[0]?.currency || 'USD')}
                </div>
                <div className="text-purple-100 text-xs">Across All Wallets</div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <div>
                  <div className="text-purple-100">Active Wallets</div>
                  <div className="font-semibold text-white">{wallets.length}</div>
                </div>
                <div className="text-right">
                  <div className="text-purple-100">Total Transactions</div>
                  <div>{transactions.length}</div>
                </div>
              </div>
            </div>
          </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={selectedWallet}
                  onChange={(e) => setSelectedWallet(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                >
                  <option value="all">All Wallets</option>
                  {wallets.map(wallet => (
                    <option key={wallet.id} value={wallet.id}>
                      {wallet.currency} Wallet (••••{wallet.virtual_card_number.slice(-4)})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Transaction History</h2>
          </div>

          {loading ? (
            <LoadingScreen fullscreen={false} message="Loading transaction history..." />
          ) : error ? (
            <div className="p-8 text-center">
              <div className="text-red-600 mb-2">{error}</div>
              <button
                onClick={fetchWalletData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No transactions found</h3>
              <p className="text-gray-600">No wallet transactions for the selected period</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {transactions.map((transaction) => (
                <div key={transaction.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${getTransactionTypeColor(transaction.transfer_type)}`}>
                        {getTransactionIcon(transaction.transfer_type)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {transaction.transfer_type}
                        </div>
                        <div className="text-sm text-gray-500">
                          {transaction.description || transaction.reference}
                        </div>
                      </div>
                    </div>

                    <div className="text-right">
                      <div className={`font-bold ${transaction.transfer_type === 'DEPOSIT' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.transfer_type === 'DEPOSIT' ? '+' : '-'}
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(transaction.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-6 text-sm text-gray-500">
                    <div>
                      <span className="font-medium">Previous:</span>{' '}
                      {formatCurrency(transaction.previous_balance, transaction.currency)}
                    </div>
                    <div>
                      <span className="font-medium">New:</span>{' '}
                      {formatCurrency(transaction.new_balance, transaction.currency)}
                    </div>
                    <div>
                      <span className="font-medium">Status:</span>{' '}
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        transaction.status === 'COMPLETED' 
                          ? 'bg-green-100 text-green-800'
                          : transaction.status === 'PENDING'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
    </div>
  );
};

export default WalletHistory;